"use client";

import { useEffect, useRef, useState } from "react";
import { TextField, Box, Autocomplete, Button, CircularProgress } from "@mui/material";
import HEADER_RSLHOME from "../../data/header-rslhome";
import { countries } from "../../data/countries";
import CtaButton from "../../components/buttonWithArrow";
import { useSearchParams } from "next/navigation";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { InputAdornment, IconButton } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { submitOtherEnquiry, validateReferralCode } from "../../redux/slices/real-estate";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export const normalizeCountryCode = (code) => {
    if (!code) return "";
    return code.startsWith("+") ? code : `+${code}`;
};


export default function PropertyReferralPage() {
    const dispatch = useDispatch();
    const searchParams = useSearchParams();
    const { verifyReferralRes, enquirySubmitRes } = useSelector(
        (state) => state.realEstate
    );
    const referralCode = searchParams.get("referral_code");
    const driverId = searchParams.get("driver_id");
    const [loading, setLoading] = useState(false);
    const [referralLoader, setReferralLoader] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState("success"); // success | error
    const [dialogMessage, setDialogMessage] = useState("");
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        mobile: "",
        countryCode: "971",
        message: "",
        driverRefferal: referralCode || "",
        driverId: driverId || "",
        isFromPropertyReferral: false,
    });
    const [selectedCountry, setSelectedCountry] = useState(
        countries.find(c => c.phone === formData.countryCode) || countries[0]
    );
    const formRef = useRef(null);
    const [showScrollArrow, setShowScrollArrow] = useState(true);
    const lastValidatedRef = useRef("");


    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShowScrollArrow(false);
                } else {
                    setShowScrollArrow(true);
                }
            },
            {
                threshold: 0.3, // form visible by 30%
            }
        );

        if (formRef.current) {
            observer.observe(formRef.current);
        }

        return () => {
            if (formRef.current) observer.unobserve(formRef.current);
        };
    }, []);

    const validateDriverReferral = (code) => {
        if (!code || code === lastValidatedRef.current) return;

        lastValidatedRef.current = code;
        setReferralLoader(true);
        dispatch(validateReferralCode({ referral_code: code }));
    };


    useEffect(() => {
        if (referralCode) {
            validateDriverReferral(referralCode);
        }
    }, [referralCode]);

    useEffect(() => {
        if (!verifyReferralRes) return;

        setReferralLoader(false);

        if (verifyReferralRes.status === 1) {
            setFormData((prev) => ({
                ...prev,
                isFromPropertyReferral: true,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                isFromPropertyReferral: false,
            }));
        }
    }, [verifyReferralRes]);


    const [errors, setErrors] = useState({});

    /* ---------------- VALIDATION ---------------- */
    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        }

        if (!formData.email) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Enter a valid email";
        }

        if (!formData.mobile) {
            newErrors.mobile = "Phone number is required";
        } else if (!/^\d{7,15}$/.test(formData.mobile)) {
            newErrors.mobile = "Enter valid phone number";
        }

        if (!formData.driverRefferal) {
            newErrors.driverRefferal = "Driver referral code is required";
        } else if (!formData.isFromPropertyReferral) {
            newErrors.driverRefferal = "Invalid referral code";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const reset = () => {
        setLoading(false);
        if (referralCode) {
            lastValidatedRef.current = null;
            validateDriverReferral(referralCode);
        }
        setFormData({
            name: "",
            email: "",
            mobile: "",
            countryCode: "971",
            message: "",
            driverRefferal: referralCode,
            driverId: "",
        });
    }

    /* ---------------- SUBMIT ---------------- */
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setLoading(true);
        const { isFromPropertyReferral, ...rest } = formData;
        const obj = {
            ...rest,
            referralCode: formData.driverRefferal,
            countryCode: normalizeCountryCode(formData.countryCode),
            "latitude": 0.0,
            "longitude": 0.0,
            "propertyType": [],
            "rooms": [],
            "location": "",
            "propertyTypePreference": "",
            "paymentPreference": "",
            "budget": null,
            "userId": "",
            "isDriverReferral": true,
        }
        dispatch(submitOtherEnquiry(obj));
    };

    useEffect(() => {
        if (!enquirySubmitRes) return;

        setLoading(false);

        if (enquirySubmitRes.status === 1) {

            setDialogType("success");
            setDialogMessage("Enquiry submitted successfully!");
            setTimeout(() => {
                reset();
            }, 800);
        } else {

            setDialogType("error");
            setDialogMessage(
                enquirySubmitRes.message || "Failed to submit enquiry. Please try again."
            );
        }

        setDialogOpen(true);
    }, [enquirySubmitRes]);


    return (
        <div className="referral-wrapper">
            <div className="referral-card">

                {/* LEFT SECTION */}
                <div className="referral-about">
                    <img
                        src={HEADER_RSLHOME?.logo_white?.src}
                        alt="Company Logo"
                        className="company-logo"
                    />

                    <div>
                        <h1>RSL Real Estate Assistance</h1>
                        <p className="subtitle">
                            Professional property support, delivered with excellence.
                        </p>
                    </div>

                    <p className="description">
                        You have been referred by an RSL professional to connect with our
                        Real Estate Assistance team. Share your property requirements and
                        our specialists will help you find premium residential and
                        commercial properties across verified locations.
                    </p>

                    <ul className="features">
                        <li>Curated residential & commercial properties</li>
                        <li>Verified listings in prime locations</li>
                        <li>Expert property consultation</li>
                        <li>Complete end-to-end assistance</li>
                    </ul>

                    <span className="note">
                        This page is used solely to receive your property requirements.
                        An RSL representative will contact you directly with suitable options.
                    </span>
                </div>


                {/* RIGHT SECTION – FORM */}
                <div className="referral-form" ref={formRef}>
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <h2>Property Enquiry Form</h2>

                        <TextField
                            label="Name"
                            fullWidth
                            value={formData.name}
                            error={!!errors.name}
                            helperText={errors.name}
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");

                                setFormData((prev) => ({ ...prev, name: value }));

                                setErrors((prev) => {
                                    if (!prev.name) return prev;
                                    const { name, ...rest } = prev;
                                    return rest;
                                });
                            }}
                            sx={inputStyle}

                        />

                        {/* PHONE */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                mt: 2,
                                flexDirection: { xs: "column", sm: "row" },
                            }}
                        >
                            <Autocomplete
                                disableClearable
                                options={countries}
                                getOptionLabel={(o) => `${o.code} (+${o.phone})`}
                                value={selectedCountry}
                                onChange={(e, val) => {
                                    if (!val) return;

                                    setSelectedCountry(val);
                                    setFormData((prev) => ({
                                        ...prev,
                                        countryCode: val.phone,
                                        mobile: "",
                                    }));

                                    // clear mobile error
                                    setErrors((prev) => {
                                        const { mobile, ...rest } = prev;
                                        return rest;
                                    });
                                }}
                                sx={{ width: { xs: "100%", sm: "35%", md: "42%" } }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Code" sx={inputStyle} />
                                )}
                            />

                            <TextField
                                label="Phone Number"
                                fullWidth
                                value={formData.mobile}
                                error={!!errors.mobile}
                                helperText={errors.mobile}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "");

                                    setFormData((prev) => ({
                                        ...prev,
                                        mobile: value,
                                    }));

                                    // clear phone error
                                    setErrors((prev) => {
                                        const { mobile, ...rest } = prev;
                                        return rest;
                                    });
                                }}
                                inputProps={{
                                    maxLength: Number(selectedCountry?.maxlength || 15),
                                }}
                                sx={inputStyle}
                            />

                        </Box>

                        <TextField
                            label="Email"
                            fullWidth
                            value={formData.email}
                            error={!!errors.email}
                            helperText={errors.email}
                            onChange={(e) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    email: e.target.value,
                                }));

                                // clear email error
                                setErrors((prev) => {
                                    const { email, ...rest } = prev;
                                    return rest;
                                });
                            }}
                            sx={{ ...inputStyle, mt: 2 }}
                        />

                        <TextField
                            label="Driver Referral Code"
                            fullWidth
                            value={formData.driverRefferal}
                            error={!!errors.driverRefferal}
                            helperText={errors.driverRefferal}
                            onChange={(e) => {
                                const value = e.target.value;

                                setFormData((prev) => ({
                                    ...prev,
                                    driverRefferal: value,
                                    isFromPropertyReferral: false, // reset until validated
                                }));

                                // clear referral error
                                setErrors((prev) => {
                                    const { driverRefferal, ...rest } = prev;
                                    return rest;
                                });
                            }}
                            onBlur={() => {
                                if (formData.driverRefferal?.trim()) {
                                    validateDriverReferral(formData.driverRefferal.trim());
                                }
                            }}
                            sx={{ ...inputStyle, mt: 2 }}
                            InputProps={{
                                readOnly: referralCode,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton disabled edge="end" sx={{ cursor: "default" }}>
                                            {referralLoader ? (
                                                <CircularProgress size={18} sx={{ color: 'black' }} />
                                            ) : formData.isFromPropertyReferral ? (
                                                <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#0D8196" }} />
                                            ) : (
                                                <HighlightOffIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            label="Message (optional)"
                            fullWidth
                            multiline
                            rows={4}
                            sx={{
                                ...inputStyle,
                                mt: 2,
                            }}
                            value={formData.message}
                            onChange={(e) =>
                                setFormData({ ...formData, message: e.target.value })
                            }
                        />

                        <div style={{ display: "flex", justifyContent: "end", marginTop: "20px", width: '100%' }}>
                            <CtaButton type="submit" cta={{ label: "Submit" }} loader={loading} />
                        </div>
                    </Box>

                    {showScrollArrow && (
                        <Box
                            onClick={() => {
                                formRef.current?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                });
                            }}
                            sx={{
                                position: "fixed",
                                bottom: 20,
                                right: "50%",
                                transform: "translateX(50%)",
                                zIndex: 1300,
                                display: { xs: "flex", md: "none" },
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                background: "#ffffff",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
                                cursor: "pointer",
                                animation: "bounce 1.8s infinite",
                            }}
                        >
                            <KeyboardArrowDownIcon sx={{ color: "#0D8196", fontSize: 30 }} />
                        </Box>
                    )}


                </div>
            </div>

            <Dialog
                open={dialogOpen}
                onClose={(event, reason) => {
                    if (reason === "backdropClick" || reason === "escapeKeyDown") {
                        return;
                    }
                    setDialogOpen(false);
                }}
                disableEscapeKeyDown
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "20px",
                        p: 1,
                    },
                }}
            >
                <DialogContent
                    sx={{
                        textAlign: "center",
                        pt: 4,
                        pb: 3,
                    }}
                >
                    {/* ICON */}
                    <Box
                        sx={{
                            mx: "auto",
                            mb: 2,
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                                dialogType === "success"
                                    ? "rgba(46, 125, 50, 0.12)"
                                    : "rgba(211, 47, 47, 0.12)",
                        }}
                    >
                        {dialogType === "success" ? (
                            <CheckCircleOutlineIcon sx={{ fontSize: 36, color: "#2e7d32" }} />
                        ) : (
                            <HighlightOffIcon sx={{ fontSize: 36, color: "#d32f2f" }} />
                        )}
                    </Box>

                    {/* TITLE */}
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            mb: 1,
                            color: "#101828",
                        }}
                    >
                        {dialogType === "success" ? "Submission Successful" : "Submission Failed"}
                    </Typography>

                    {/* MESSAGE */}
                    <Typography
                        sx={{
                            color: "#475467",
                            fontSize: 14,
                            lineHeight: 1.6,
                            px: 1,
                        }}
                    >
                        {dialogMessage}
                    </Typography>
                </DialogContent>

                <DialogActions
                    sx={{
                        pb: 3,
                        px: 3,
                        justifyContent: "center",
                    }}
                >
                    <Button
                        onClick={() => setDialogOpen(false)}
                        variant="contained"
                        sx={{
                            minWidth: 120,
                            borderRadius: "999px",
                            textTransform: "none",
                            fontWeight: 600,
                            background:
                                dialogType === "success"
                                    ? "linear-gradient(to top, #0D8196, #75C4C5)"
                                    : "#d32f2f",
                            "&:hover": {
                                opacity: 0.9,
                            },
                        }}
                    >
                        Okay
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
}

/* ---------------- BUTTON STYLE ---------------- */

const inputStyle = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "14px",

        /* Default border */
        "& fieldset": {
            borderColor: "#d0d5dd",
        },

        /* Hover */
        "&:hover fieldset": {
            borderColor: "#3BA5AF",
        },

        /* Focus */
        "&.Mui-focused fieldset": {
            borderColor: "#0D8196",
            borderWidth: "1px",
        },

        /* Error */
        "&.Mui-error fieldset": {
            borderColor: "#d32f2f",
        },
    },

    /* Label default */
    "& .MuiInputLabel-root": {
        color: "#667085",
    },

    /* Label on focus */
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#0D8196",
        fontWeight: 600,
    },

    /* Label error */
    "& .MuiInputLabel-root.Mui-error": {
        color: "#d32f2f",
    },

    /* Helper text */
    "& .MuiFormHelperText-root": {
        marginLeft: "4px",
    },
};
